import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import SafeRichText from "../components/SafeRichText";
import LoadingSpinner from "../components/LoadingSpinner";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';

function OurStory() {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/about`, {
      params: { cb: Date.now() }
    })
      .then(res => {
        setAboutData(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("About API Error:", error);
        setAboutData(null);
        setLoading(false);
      });
  }, []);
  
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [approachImgError, setApproachImgError] = useState(false);
  const [offerImgError, setOfferImgError] = useState(false);

  // buildImageUrl accepts either a field name like 'our_approach'
  // or a direct URL/path string (e.g. aboutData.our_approach_url)
  const buildImageUrl = (fieldOrUrl) => {
    if (!fieldOrUrl || !aboutData) return null;

    // If caller passed a full URL/path (contains '/' or starts with blob/http), use that
    if (typeof fieldOrUrl === 'string' && (fieldOrUrl.startsWith('blob:') || fieldOrUrl.startsWith('http') || fieldOrUrl.startsWith('/'))) {
      // If it starts with '/', prefix with API base and encode the path
      if (fieldOrUrl.startsWith('/')) return `${API_BASE_URL}${encodeURI(fieldOrUrl)}`;
      return fieldOrUrl;
    }

    // Otherwise treat as a field name and look for `${field}_url` on aboutData
    const urlKey = `${fieldOrUrl}_url`;
    const val = aboutData[urlKey];
    if (!val) return null;
    if (typeof val === 'string' && val.startsWith('blob:')) return val;
    if (typeof val === 'string' && val.startsWith('/')) return `${API_BASE_URL}${encodeURI(val)}`;
    return val;
  };

  const isImageData = (fieldName) => {
    if (!fieldName || !aboutData) return false;
    
    const urlKey = `${fieldName}_url`;
    return !!aboutData[urlKey];
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => setMousePosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const heroVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: "easeOut" } }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.25, 0.46, 0.45, 0.94] } }
  };

  return (
    <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-sm sm:text-base overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
      {loading ? (
        <LoadingSpinner message="Loading about content..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={24} />
      ) : (
      <div className="min-h-screen relative">
        <div className="fixed inset-0 bg-slate-50" />

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center mb-6 lg:mb-8" initial="hidden" animate="visible" variants={heroVariants}>
              
              
            </motion.div>

            {/* Who We Are Section */}
            {aboutData?.who_we_are && (
              <motion.div className="mb-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                <div className="relative z-10">
                  <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">
                    Who We Are
                  </motion.h2>
                  <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl shadow-inner border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                    <SafeRichText content={aboutData.who_we_are} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Hardcoded sections (match Who We Are styling, avoid double frames) */}
            {aboutData && (
              <div className="w-full max-w-6xl mx-auto">
                {/* Our Story */}
                {aboutData.our_story && (
                  <motion.div className="mb-24" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">Our Story</motion.h2>
                    <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                      <SafeRichText content={aboutData.our_story} />
                    </div>
                  </motion.div>
                )}

                {/* Our Mission */}
                {aboutData.our_mission && (
                  <motion.div className="mb-24" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">Our Mission</motion.h2>
                    <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                      <SafeRichText content={aboutData.our_mission} />
                    </div>
                  </motion.div>
                )}

                {/* Our Mandate */}
                {aboutData.our_mandate && (
                  <motion.div className="mb-24" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">Our Mandate</motion.h2>
                    <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                      <SafeRichText content={aboutData.our_mandate} />
                    </div>
                  </motion.div>
                )}

                {/* Why Us */}
                {aboutData.why_us && (
                  <motion.div className="mb-24" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">Why Us</motion.h2>
                    <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                      <SafeRichText content={aboutData.why_us} />
                    </div>
                  </motion.div>
                )}

                {/* Our Approach */}
                {true && (
                  <motion.div className="mb-24" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">Our Approach</h2>
                    <div className="w-full max-w-3xl mx-auto mb-8">
                      <img
                        src={buildImageUrl(aboutData.our_approach_url)}
                        alt="Our Approach"
                        className="w-full rounded-2xl shadow-lg border border-emerald/30 bg-white"
                      />
                    </div>
                  </motion.div>
                )}

                {/* What We Offer */}
                {(aboutData.what_we_offer !== undefined && aboutData.what_we_offer !== null) && (
                  <motion.div className="mb-24" style={{ display: 'none' }} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={sectionVariants}>
                    <motion.h2 className="text-4xl md:text-5xl font-bold mb-8 text-center tracking-tight text-black">What We Offer</motion.h2>
                    {(() => {
                      const isImage = isImageData('what_we_offer');
                      const imageUrl = isImage ? buildImageUrl('what_we_offer') : null;
                      console.log('What We Offer - isImage:', isImage, 'imageUrl:', imageUrl, 'what_we_offer_url:', aboutData.what_we_offer_url, 'raw value:', aboutData.what_we_offer);
                      
                      if (imageUrl && !offerImgError && isImage) {
                        return (
                          <div className="w-full max-w-3xl mx-auto mb-8">
                            <img
                              src={imageUrl}
                              alt="What We Offer"
                              className="w-full rounded-2xl shadow-lg border border-emerald/30 bg-white"
                              onError={(e) => {
                                console.error('What We Offer image failed to load:', e.target.src);
                                setOfferImgError(true);
                              }}
                              onLoad={() => console.log('What We Offer image loaded successfully:', imageUrl)}
                            />
                          </div>
                        );
                      }
                      
                      // Show image not found if it was supposed to be an image but failed
                      if (offerImgError && isImage) {
                        return (
                          <div className="w-full max-w-3xl mx-auto mb-8">
                            <div className="rounded-2xl shadow-lg border border-emerald/30 bg-gray-100 p-12 text-center">
                              <div className="text-5xl mb-4">🖼️</div>
                              <p className="text-gray-600 text-lg">Image Not Found</p>
                              <p className="text-gray-500 text-sm mt-2">The image for this section could not be loaded.</p>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl border border-emerald/20 p-6 md:p-8 text-base md:text-lg text-black leading-relaxed whitespace-pre-line" style={{textAlign: 'justify'}}>
                          <SafeRichText content={aboutData.what_we_offer} />
                        </div>
                      );
                    })()}
                  </motion.div>
                )}
              </div>
            )}

            {!aboutData && !loading && (
              <motion.div className="text-center py-16" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 max-w-md mx-auto border border-emerald/30">
                  <div className="text-5xl mb-6">📋</div>
                  <h3 className="text-2xl font-bold text-black mb-3">No About Data Available</h3>
                  <p className="text-black">Company information will be displayed here once it's added.</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default OurStory;
